import axios, { AxiosResponse } from 'axios';

/**
 * Impersonation Data Interface
 */
export interface ImpersonationData {
  token: string;
  tokenType: string;
  expiresIn: number;
  expiresAt: string;
  sessionUuid: string;
  candidate: {
    uuid: string;
    name: string;
    email: string;
  };
  isImpersonation: boolean;
}

/**
 * Impersonation Token Response Interface
 */
export interface ImpersonationTokenResponse {
  status: string;
  message: string;
  data: {
    token: string;
    token_type: string;
    expires_in: number;
    expires_at: string;
    session_uuid: string;
    candidate: {
      uuid: string;
      name: string;
      email: string;
    };
    is_impersonation: boolean;
  };
}

/**
 * Active Sessions Response Interface
 */
export interface ActiveSessionsResponse {
  status: string;
  message: string;
  data: {
    sessions: Array<{
      session_uuid: string;
      candidate: {
        uuid: string;
        name: string;
        email: string;
      };
      started_at: string;
      expires_at: string;
      is_valid: boolean;
    }>;
  };
}

/**
 * Impersonation History Response Interface
 */
export interface ImpersonationHistoryResponse {
  status: string;
  message: string;
  data: {
    candidate_uuid: string;
    history: Array<{
      session_uuid: string;
      impersonator: {
        uuid: string;
        name: string;
        email: string;
      };
      started_at: string;
      ended_at: string | null;
      status: 'active' | 'expired' | 'revoked';
      reason: string | null;
    }>;
  };
}

/**
 * Revoke Session Response Interface
 */
export interface RevokeSessionResponse {
  status: string;
  message: string;
  data: [];
}

const IMPERSONATION_STORAGE_KEY = 'impersonation';

/**
 * Impersonation Service
 * Handles all API calls related to the impersonation feature
 */
const ImpersonationService = {
  /**
   * Generate an impersonation token for a candidate
   * @param candidateUuid - The UUID of the candidate to impersonate
   * @param reason - Optional reason for impersonation (for audit trail)
   * @returns Promise with impersonation token data
   */
  generateToken: async (
    candidateUuid: string,
    reason?: string
  ): Promise<ImpersonationTokenResponse> => {
    const response: AxiosResponse<ImpersonationTokenResponse> = await axios.post(
      `/v1/admin/impersonate/${candidateUuid}`,
      reason ? { reason } : {}
    );
    return response.data;
  },

  /**
   * Get all active impersonation sessions for the current admin
   * @returns Promise with active sessions data
   */
  getActiveSessions: async (): Promise<ActiveSessionsResponse> => {
    const response: AxiosResponse<ActiveSessionsResponse> = await axios.get(
      '/v1/admin/impersonate/sessions'
    );
    return response.data;
  },

  /**
   * Revoke an active impersonation session
   * @param sessionUuid - The UUID of the session to revoke
   * @returns Promise with revoke response
   */
  revokeSession: async (sessionUuid: string): Promise<RevokeSessionResponse> => {
    const response: AxiosResponse<RevokeSessionResponse> = await axios.delete(
      `/v1/admin/impersonate/sessions/${sessionUuid}`
    );
    return response.data;
  },

  /**
   * Get impersonation history for a specific candidate
   * @param candidateUuid - The UUID of the candidate
   * @returns Promise with impersonation history data
   */
  getImpersonationHistory: async (
    candidateUuid: string
  ): Promise<ImpersonationHistoryResponse> => {
    const response: AxiosResponse<ImpersonationHistoryResponse> = await axios.get(
      `/v1/admin/impersonate/history/${candidateUuid}`
    );
    return response.data;
  },

  /**
   * Store impersonation data in sessionStorage
   * @param data - The impersonation data to store
   */
  storeImpersonationData: (data: ImpersonationData): void => {
    sessionStorage.setItem(IMPERSONATION_STORAGE_KEY, JSON.stringify(data));
  },

  /**
   * Get impersonation data from sessionStorage
   * @returns The stored impersonation data or null
   */
  getStoredImpersonationData: (): ImpersonationData | null => {
    const data = sessionStorage.getItem(IMPERSONATION_STORAGE_KEY);
    if (data) {
      try {
        return JSON.parse(data) as ImpersonationData;
      } catch {
        return null;
      }
    }
    return null;
  },

  /**
   * Clear impersonation data from sessionStorage
   */
  clearImpersonationData: (): void => {
    sessionStorage.removeItem(IMPERSONATION_STORAGE_KEY);
  },

  /**
   * Check if currently in impersonation mode
   * @returns boolean indicating if in impersonation mode
   */
  isImpersonating: (): boolean => {
    const data = ImpersonationService.getStoredImpersonationData();
    if (!data) return false;

    // Check if token has expired
    const expiresAt = new Date(data.expiresAt);
    const now = new Date();
    if (now >= expiresAt) {
      ImpersonationService.clearImpersonationData();
      return false;
    }

    return data.isImpersonation;
  },

  /**
   * Get the impersonation token if valid
   * @returns The impersonation token or null
   */
  getImpersonationToken: (): string | null => {
    const data = ImpersonationService.getStoredImpersonationData();
    if (!data) return null;

    // Check if token has expired
    const expiresAt = new Date(data.expiresAt);
    const now = new Date();
    if (now >= expiresAt) {
      ImpersonationService.clearImpersonationData();
      return null;
    }

    return data.token;
  },

  /**
   * Open candidate onboarding in a new tab with impersonation token
   * @param candidateUuid - The UUID of the candidate
   * @param reason - Optional reason for impersonation
   * @returns Promise that resolves when the new tab is opened
   */
  accessOnboarding: async (
    candidateUuid: string,
    reason?: string
  ): Promise<void> => {
    const response = await ImpersonationService.generateToken(candidateUuid, reason);

    if (response.status === 'success') {
      // Build URL for the impersonation entry page
      // This page will handle token setup and redirect to the candidate profile
      const redirectPath = encodeURIComponent('/candidate/my-profile/');
      const onboardingUrl = `${window.location.origin}/impersonate?impersonation_token=${encodeURIComponent(response.data.token)}&session=${encodeURIComponent(response.data.session_uuid)}&redirect=${redirectPath}`;

      // Open new tab with the impersonation entry URL
      window.open(onboardingUrl, '_blank');
    }
  },
};

export default ImpersonationService;
